import {
    all,
    call,
    debounce,
    getContext,
    put,
    select,
    setContext,
    take,
    takeEvery,
    takeLatest,
} from 'redux-saga/effects';

import { actions as uiActions, types as uiTypes } from '@elab/core/store/ui';
import { selectors as configSelectors } from '@elab/core/store/config';
import {
    cleanupAddressFields,
    collectSuggestionsData,
    isConfirmationDialogEnabled,
    onChangeFormAddressField,
    resetAddressValidation,
    setFieldValues,
} from '@elab/core/utils/address/helpers';
import { getActions as getDisplayOneByOneActions } from '@elab/components/connected/DisplayOneByOne/redux';

import api from './api';

export function* getAddressSuggestions({ actions }, dto, userId, reduxPath = '') {
    try {
        const response = (yield call(api.getAddressSuggestions, userId, dto)) || {};

        const { line1, town } = (response.suggestedAddresses && response.suggestedAddresses[0]) || {
            line1: '',
            town: '',
        };

        yield all([
            put(actions.setDataFieldValue(`${reduxPath}line1`, line1, true)),
            put(actions.setDataFieldValue(`${reduxPath}town`, town, true)),
        ]);
    } catch (e) {
        /* Nothing */
    }
}

function* openModal(modalName, confirmAddressValidationAction) {
    const { notBlockingDialog } = yield select(configSelectors.getAddressVerificationDialogConfig);

    yield put(uiActions.showModal(modalName));
    const modalAction = yield take([uiTypes.OK_MODAL, uiTypes.CANCEL_MODAL]);

    if (notBlockingDialog || modalAction.type === uiTypes.OK_MODAL) {
        yield put(confirmAddressValidationAction);
    }

    if (modalAction.type === uiTypes.OK_MODAL) {
        return true;
    }
}

export function* checkAddressVerificationModal(dialogId, confirmAction, isValidSelect) {
    const isAddressValid = yield select(isValidSelect);

    if (isAddressValid) {
        return true;
    }

    return yield call(openModal, dialogId, confirmAction);
}

export function* getConfirmationFromModal({ types, actions, selectors }) {
    const isEnabled = yield call(isConfirmationDialogEnabled);

    const step = yield select(state => selectors.getStep(state));
    const metadata = yield select(state => selectors.getMetadata(state));
    // TODO: refactor, remove hardcoded input name
    const hasAddressOnCurrentStep = metadata.length >= step && metadata[step - 1].fields.postalCode;

    if (isEnabled && hasAddressOnCurrentStep) {
        let canContinue = yield checkAddressVerificationModal(
            types.ADDRESS_VERIFICATION_FAILED_DIALOG,
            actions.setAddressValidation(true),
            selectors.isAddressValid,
        );

        if (!canContinue) {
            return false;
        }

        // For nested forms should be used a separate Modal
        canContinue = yield checkAddressVerificationModal(
            types.ADDRESS_NESTED_VERIFICATION_FAILED_DIALOG,
            actions.setAddressValidation(true, true),
            selectors.isNestedAddressValid,
        );

        if (!canContinue) {
            return false;
        }
    }

    return true;
}

export function* selectAddressSuggestion(action) {
    const { suggestion, nestedFormReduxPath } = action.payload;

    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');

    const { countrySpecificConfig } = yield collectSuggestionsData({
        actions,
        selectors,
        nestedFormReduxPath,
    });

    if (countrySpecificConfig) {
        const fieldsToUpdate = countrySpecificConfig.postalCodeAutocomplete
            ? [...countrySpecificConfig.targetFields, 'postalCode']
            : countrySpecificConfig.targetFields;

        yield setFieldValues(
            { nestedFormReduxPath, actions },
            fieldsToUpdate,
            suggestion,
            countrySpecificConfig.saveSuggestedValues,
        );
    }

    yield put(actions.setAddressValidation(true, !!nestedFormReduxPath));

    yield put(actions.hideAddressSuggestionsList());
}

export function* addressInput(action) {
    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');

    const { isAddressField } = yield collectSuggestionsData({
        action,
        actions,
        selectors,
    });

    if (!action.payload.prefill) {
        yield cleanupAddressFields(action, actions, selectors);
    }

    if (isAddressField) {
        yield resetAddressValidation(action, actions, selectors);
        yield put(actions.changeAddressField(action.payload));
    }
}

export function* autofillAddressFields({ nestedFormReduxPath, countrySpecificConfig }) {
    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');
    const suggestions = yield select(selectors.getAddressSuggestions);

    if (suggestions.length > 0) {
        const fieldsToUpdate = countrySpecificConfig.postalCodeAutocomplete
            ? [...countrySpecificConfig.targetFields, 'postalCode']
            : countrySpecificConfig.targetFields;

        yield setFieldValues({ nestedFormReduxPath, actions }, fieldsToUpdate, suggestions[0]);

        yield put(actions.setAddressValidation(true, !!nestedFormReduxPath));
    }
}

export function* hideSuggestions({ payload: name }) {
    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');

    const data = yield collectSuggestionsData({
        action: { payload: { name } },
        actions,
        selectors,
    });

    if (!data.isAddressField) {
        return;
    }

    if (data.countrySpecificConfig && data.countrySpecificConfig.applySuggestionOnBlur) {
        yield autofillAddressFields(data);
    }
    yield put(actions.hideAddressSuggestionsList());
}

export function* fillWithSaved({ payload: name }) {
    const actions = yield getContext('actions');
    const selectors = yield getContext('selectors');
    const dataField = yield select(selectors.getDataField, name);

    if (dataField && dataField.savedValue) {
        yield put(actions.setDataFieldValue(name, dataField.savedValue, true));
    }
}

export function* initAddressSuggestionWatchers(reduxArtifacts) {
    const { types } = reduxArtifacts;

    yield setContext(reduxArtifacts);
    yield all([
        takeLatest(types.SELECT_ADDRESS_SUGGESTION, selectAddressSuggestion),
        takeEvery(types.SET_DATA_FIELD_VALUE, addressInput),
        debounce(1000, types.CHANGE_ADDRESS_FIELD, onChangeFormAddressField),
        debounce(200, types.BLUR_FIELD, hideSuggestions),
        debounce(200, types.BLUR_FIELD, fillWithSaved),
    ]);
}

export function copySecondAddressFields(actions, address) {
    return field =>
        put(
            actions.setDataFieldValue(
                `addAddress.value.0.${field}`,
                (field === 'country' ? address.country.isocode : address[field]) || '',
                true,
                true,
            ),
        );
}

export function* defineAdditionalAddressFieldsDisplay({
    storeName,
    valuesSource = {},
    extractValuesWith,
}) {
    const hiddenFieldNames = yield select(configSelectors.getHiddenAddressFieldNames);
    const { setItemsToShow } = yield call(getDisplayOneByOneActions, storeName);

    const useExtract = typeof extractValuesWith === 'function';

    const showAdditionalAddressFields = hiddenFieldNames.some(fieldName =>
        useExtract ? extractValuesWith(valuesSource, fieldName) : valuesSource[fieldName],
    );

    if (showAdditionalAddressFields) {
        yield put(setItemsToShow(hiddenFieldNames.length));
    }
}

export function* setFakeValidationOnAddressTypeChange(selectors, actions) {
    const formData = yield select(selectors.getData);
    const hasAddressType = formData.hasOwnProperty('addressType');
    if (hasAddressType) {
        const selectedAddressType = formData.addressType.value;
        for (let fieldName in formData) {
            const fieldData = formData[fieldName];
            const hasWhenDefinedProp = fieldData.hasOwnProperty('whenDefined');
            const whenDefined = hasWhenDefinedProp && fieldData.whenDefined;
            const whenDefinedContent = whenDefined && whenDefined.length > 0 && whenDefined[0];
            const addressTypeArr = whenDefinedContent && whenDefinedContent.split('.');
            const whenDefinedAddressType = addressTypeArr[addressTypeArr.length - 1];
            const shouldSetFakeValidattion =
                whenDefinedAddressType && selectedAddressType !== whenDefinedAddressType;
            if (shouldSetFakeValidattion) {
                const payload = {
                    name: fieldName,
                    validations: {
                        required: false,
                        constraints: [],
                        value: fieldData.value,
                    },
                };
                yield put(actions.setFakeValidation(payload));
            } else {
                yield put(actions.setDataFieldError(fieldName, ''));
            }
        }
    }
}
